import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import FilterCenterFocusIcon from "@mui/icons-material/FilterCenterFocus"
import { Accordion, AccordionDetails, AccordionSummary, Box, Tooltip, Typography } from "@mui/material"
import React, { useCallback, useContext } from "react"
import { useNavigate } from "react-router-dom"
import { customPalette } from "../../../../../../../../theme"
import { IconPersonnalisation } from "../../../../../../../components/custom-icons/icon-personnalisation"
import { IconQuestionnaire } from "../../../../../../../components/custom-icons/icon-questionnaires"
import { theme } from "../../../../../../../components/layout/BSLayout"
import { ErrorContext } from "../../../../../../../components/layout/error-snackbar"
import { pagesUrl } from "../../../../../../../core/appConstants"
import { BSBuildingResultContext } from "../../../../../../../core/context/beem-shot/BSBuildingResult/BSBuildingResultContext"
import { BSInputContext } from "../../../../../../../core/context/beem-shot/BSInput/BSInputContext"
import { BSVariantContext } from "../../../../../../../core/context/beem-shot/BSVariant/BSVariantContext"
import { ProjectContext } from "../../../../../../../core/context/project/ProjectContext"
import { CalculStatusEnum } from "../../../../../../../core/enum/calculStatusEnum"
import { usageEnumLabels } from "../../../../../../../core/enum/usageEnum"
import { resolveUrl } from "../../../../../../../core/services/http-service"
import { WarningUpdatedDialogContext } from "../../components/Dialog/WarningUpdatedDialogContext"
import { BSMorphologyContext } from "../../tabs/BSBuildingTabs/BSBuildingMorphologyTab/context/BSMorphologyContext"
import { BSLevelContext } from "../BSCreateBuildingPages/BSZoneAndLevelPage/context/BSLevelContext"
import { BSBuildingContext } from "./context/BSBuildingContext"

interface IProps {
  isDisabled: boolean
  showWarning: boolean
}

export function BSUsageAccordion({ isDisabled, showWarning }: Readonly<IProps>): React.JSX.Element {
  const navigate = useNavigate()

  const { project } = useContext(ProjectContext)
  const { selectedVariant } = useContext(BSVariantContext)
  const { selectedBSBuilding } = useContext(BSBuildingContext)
  const { bsInput } = useContext(BSInputContext)
  const { bsBuildingResult } = useContext(BSBuildingResultContext)
  const { groupLevelSuperstructureList } = useContext(BSLevelContext)
  const { bsMorphology, startEstimatingMorphology } = useContext(BSMorphologyContext)
  const { handleSelectUrl } = useContext(WarningUpdatedDialogContext)

  const openErrorSnackbar = useContext(ErrorContext)

  const navigateToCustomization: () => void = useCallback(() => {
    if (selectedBSBuilding?.id && selectedBSBuilding?.calculStatus !== CalculStatusEnum.READY) {
      startEstimatingMorphology(selectedBSBuilding.id)
    }
    handleSelectUrl(
      resolveUrl(pagesUrl.BEEM_SHOT_CUSTOM_MATERIALS, [project?.id, selectedVariant?.id, selectedBSBuilding?.id])
    )
  }, [
    project?.id,
    selectedBSBuilding?.calculStatus,
    selectedBSBuilding?.id,
    selectedVariant?.id,
    startEstimatingMorphology,
    handleSelectUrl,
  ])

  function getTooltipMessage(): string {
    if (!selectedBSBuilding?.bsBimModelId && selectedBSBuilding?.bim) {
      return "Veuillez importer une maquette numérique"
    }

    if (isDisabled) {
      return "Veuillez renseigner la morphologie et les niveaux"
    }

    return ""
  }

  const handleClickQuestionnaire: () => void = useCallback(() => {
    if (isDisabled) {
      return
    }
    if (groupLevelSuperstructureList?.length === 0 && !selectedBSBuilding?.bim) {
      openErrorSnackbar(new Error("Veuillez créer des niveaux avant d'accéder au questionnaire"))
    } else if (selectedBSBuilding?.bim) {
      handleSelectUrl(
        resolveUrl(pagesUrl.CALCUL, [
          project?.id,
          selectedVariant?.id,
          selectedBSBuilding?.id,
          selectedBSBuilding?.bsBimModelId,
        ])
      )
    } else {
      handleSelectUrl(
        resolveUrl(pagesUrl.CALCUL_MORPHOLOGY, [project?.id, selectedVariant?.id, selectedBSBuilding?.id, bsMorphology?.id])
      )
    }
  }, [
    bsMorphology?.id,
    groupLevelSuperstructureList?.length,
    isDisabled,
    openErrorSnackbar,
    project?.id,
    selectedBSBuilding?.bim,
    selectedBSBuilding?.bsBimModelId,
    selectedBSBuilding?.id,
    selectedVariant?.id,
    handleSelectUrl,
  ])

  return (
    <Accordion
      expanded
      disableGutters
      disabled={isDisabled}
      sx={{
        boxShadow: "none",
        fontSize: "14px",
        cursor: isDisabled ? "not-allowed" : "pointer",
        "&:hover": {
          cursor: isDisabled ? "not-allowed" : "pointer",
        },
      }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: customPalette.darkBlue3 }} />}
        sx={{
          p: 0,
          ...(isDisabled && {
            pointerEvents: "none",
            cursor: "not-allowed",
          }),
          "&.Mui-expanded": {
            marginBottom: 0,
          },
        }}>
        <Box
          sx={{
            display: "flex",
            gap: "8px",
            color: customPalette.darkBlue3,
            ...(isDisabled && {
              cursor: "not-allowed",
              "&:hover": { cursor: "not-allowed" },
            }),
          }}>
          <FilterCenterFocusIcon sx={{ color: customPalette.darkBlue3 }} />
          {bsInput?.usage && usageEnumLabels[bsInput.usage]}
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            color: theme.palette.secondary.dark,
            ...(isDisabled && {
              cursor: "not-allowed",
              "&:hover": { cursor: "not-allowed" },
            }),
          }}>
          <Tooltip title={getTooltipMessage()} arrow placement="right">
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={2}>
                <IconQuestionnaire />
                <Typography
                  variant="body2"
                  color={isDisabled ? customPalette.lightGray2 : undefined}
                  sx={{
                    whiteSpace: "normal",
                    flexGrow: 1,
                    "&:hover": { textDecoration: isDisabled ? undefined : "underline" },
                  }}
                  onClick={isDisabled ? undefined : handleClickQuestionnaire}>
                  Matérialités (simplifié+avancé)
                </Typography>
              </Box>

              {showWarning && <ErrorOutlineIcon color="error" />}
            </Box>
          </Tooltip>

          {bsBuildingResult && !isDisabled && (
            <Box display="flex" alignItems="center" gap={2}>
              <IconPersonnalisation />

              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "normal",
                  flexGrow: 1,
                  "&:hover": { textDecoration: isDisabled ? undefined : "underline" },
                }}
                onClick={isDisabled ? undefined : navigateToCustomization}>
                Personnalisation (expert)
              </Typography>
            </Box>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}
